import { lt } from '@mews/framework';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { PaymentMethodActions } from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import { useDeleteConfirmationDialog } from '@mews-commander/ui';
import { ProcessPaymentType } from '../../forms/ProcessPayment/ProcessPaymentType';
import { useProcessPaymentDialog } from '../../forms/ProcessPayment/useProcessPaymentDialog';
import { PaymentsTablesActionsMenu } from '../PaymentsTablesActionsMenu';
import { useClearPaymentCard, useDeletePaymentCard, useDisablePaymentCard } from './queries';

interface Props {
    paymentMethodActions: PaymentMethodActions[];
    paymentMethodId: string;
    accountId: string;
    paymentMethodTitle: string;
    isGatewayPaymentMethod?: boolean;
}

export const PaymentMethodsActionsMenu = ({
    paymentMethodActions,
    paymentMethodId,
    accountId,
    paymentMethodTitle,
    isGatewayPaymentMethod,
}: Props) => {
    const usePaymentDialog = (allowedPaymentTypes: ProcessPaymentType[], titleTextKey: Mews.TextKey) =>
        useProcessPaymentDialog({
            accountId,
            allowedPaymentTypes,
            title: lt(titleTextKey),
            hideSinglePaymentMethodTitle: true,
            readOnlyFormFields: {
                isPaymentMethodIdReadOnly: true,
            },
            defaultSelectedFormValues: {
                paymentMethodId,
            },
        });

    const [renderPreauthorizationDialog, togglePreauthorizationDialog] = usePaymentDialog(
        [ProcessPaymentType.GatewayCardPreauth],
        TextKey.ProcessPaymentPanelTitleForPreauthorization
    );

    const [renderExternalPreauthorizationDialog, toggleExternalPreauthorizationDialog] = usePaymentDialog(
        [ProcessPaymentType.ExternalCardPreauth],
        TextKey.ProcessPaymentPanelTitleForAddExternalPreauthorization
    );

    const [renderPaymentDialog, togglePaymentDialog] = usePaymentDialog(
        [ProcessPaymentType.GatewayPayment],
        TextKey.ProcessPaymentPanelTitleForOptions
    );

    const [renderExternalPaymentDialog, toggleExternalPaymentDialog] = usePaymentDialog(
        [ProcessPaymentType.ExternalCardPayment],
        TextKey.ProcessPaymentDialogAddExternalPaymentsTitle
    );

    const deletePaymentCard = useDeletePaymentCard({ accountId });
    const disablePaymentCard = useDisablePaymentCard({ accountId });
    const clearPaymentCard = useClearPaymentCard({ accountId });

    const { confirmationDialog: deleteConfirmationDialog, openConfirmationDialog: openDeleteConfirmationDialog } =
        useDeleteConfirmationDialog({
            deleteAction: async () => deletePaymentCard.mutateAsync({ paymentCardId: paymentMethodId }),
            renderContent: () => lt('DeleteHint'),
            title: lt('PaymentMethodDelete', { paymentMethodTitle }),
            primaryButtonTextKey: TextKey.Delete,
        });

    const { confirmationDialog: disableConfirmationDialog, openConfirmationDialog: openDisableConfirmationDialog } =
        useDeleteConfirmationDialog({
            deleteAction: async () => disablePaymentCard.mutateAsync({ paymentCardId: paymentMethodId }),
            renderContent: () => lt('DisableHint'),
            title: lt('PaymentMethodDisable', { paymentMethodTitle }),
            primaryButtonTextKey: TextKey.Disable,
        });

    const { confirmationDialog: clearConfirmationDialog, openConfirmationDialog: openClearConfirmationDialog } =
        useDeleteConfirmationDialog({
            deleteAction: async () => clearPaymentCard.mutateAsync({ paymentCardId: paymentMethodId }),
            renderContent: () => lt('ClearHint'),
            title: lt('PaymentMethodClear', { paymentMethodTitle }),
            primaryButtonTextKey: TextKey.Clear,
        });

    const actions = [
        ...(isGatewayPaymentMethod
            ? [
                  {
                      key: 'create-gateway-payment',
                      action: PaymentMethodActions.CreatePayment,
                      icon: IconName.PaymentCard,
                      text: lt('PaymentMethodActionProcessPayment'),
                      onClick: togglePaymentDialog,
                      dataTestId: DataTestId.PaymentMethodCreateGatewayPaymentActionButton,
                  },
                  {
                      key: 'create-gateway-preauthorization',
                      action: PaymentMethodActions.CreatePreauthorization,
                      icon: IconName.Preauthorization,
                      text: lt('PaymentMethodActionPreauthorize'),
                      onClick: togglePreauthorizationDialog,
                      dataTestId: DataTestId.PaymentMethodCreateGatewayPreauthorizationActionButton,
                  },
              ]
            : []),
        {
            key: 'create-external-payment',
            action: PaymentMethodActions.CreatePayment,
            icon: IconName.ExternalPayment,
            text: lt('PaymentMethodActionAddExternalPayment'),
            onClick: toggleExternalPaymentDialog,
            dataTestId: DataTestId.PaymentMethodCreateExternalPaymentActionButton,
        },
        {
            key: 'create-external-preauthorization',
            action: PaymentMethodActions.CreatePreauthorization,
            icon: IconName.ExternalPayment,
            text: lt('PaymentMethodActionAddExternalPreauthorization'),
            onClick: toggleExternalPreauthorizationDialog,
            dataTestId: DataTestId.PaymentMethodCreateExternalPreauthorizationActionButton,
        },
        {
            key: 'disable',
            action: PaymentMethodActions.Disable,
            icon: IconName.Disable,
            text: lt('Deactivate'),
            onClick: openDisableConfirmationDialog,
            dataTestId: DataTestId.PaymentMethodDisableCardActionButton,
        },
        {
            key: 'clear-data',
            action: PaymentMethodActions.ClearData,
            icon: IconName.Delete,
            text: lt('Clear'),
            onClick: openClearConfirmationDialog,
            dataTestId: DataTestId.PaymentMethodClearCardActionButton,
        },
        {
            key: 'delete',
            action: PaymentMethodActions.Delete,
            icon: IconName.Delete,
            text: lt('Delete'),
            onClick: openDeleteConfirmationDialog,
            dataTestId: DataTestId.PaymentMethodDeleteCardActionButton,
        },
    ];

    const actionsFiltred = actions.filter(({ action }) => paymentMethodActions.includes(action));

    if (actionsFiltred.length === 0) {
        return null;
    }

    return (
        <>
            <PaymentsTablesActionsMenu actions={actionsFiltred} />
            {deleteConfirmationDialog}
            {disableConfirmationDialog}
            {clearConfirmationDialog}
            {renderPreauthorizationDialog()}
            {renderExternalPreauthorizationDialog()}
            {renderPaymentDialog()}
            {renderExternalPaymentDialog()}
        </>
    );
};
